import { supabase } from '../supabaseClient';

/**
 * Fetch user profile by user ID
 * @param {string} userId - The user ID from authentication
 * @returns {Promise<Object>} User profile with id, email, first_name, last_name, is_admin
 * @throws {Error} If user ID is missing or user not found
 */
export const fetchUserProfile = async (userId) => {
  if (!userId) {
    throw new Error('User ID is required');
  }

  const { data, error } = await supabase
    .from('User')
    .select('*')
    .eq('id', userId)
    .single();

  if (error) {
    throw new Error(error.message);
  }

  return data;
};

/**
 * Fetch all users from the database
 * @returns {Promise<Array>} Array of all users
 * @throws {Error} If database query fails
 */
export const fetchAllUsers = async () => {
  const { data, error } = await supabase
    .from('User')
    .select('*')
    .order('first_name', { ascending: true });

  if (error) {
    throw new Error(error.message);
  }

  return data || [];
};

/**
 * Update user admin status
 * @param {string} userId - The user ID to update
 * @param {boolean} isAdmin - Whether user should be admin
 * @returns {Promise<Object>} Updated user object
 * @throws {Error} If user ID or admin status is missing, or if update fails
 */
export const updateUserAdmin = async (userId, isAdmin) => {
  if (!userId || isAdmin == null) {
    throw new Error('User ID and admin status are required');
  }

  const { data, error } = await supabase
    .from('User')
    .update({ is_admin: isAdmin })
    .eq('id', userId)
    .select();

  if (error) {
    throw new Error(error.message);
  }

  return data;
};

/**
 * Delete a user
 * @param {string} userId - The user ID to delete
 * @returns {Promise<void>}
 * @throws {Error} If user ID is missing, or if delete fails
 */
export const deleteUser = async (userId) => {
  if (!userId) {
    throw new Error('User ID is required');
  }

  const { error } = await supabase
    .from('User')
    .delete()
    .eq('id', userId);

  if (error) {
    throw new Error(error.message);
  }
};
